import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { get_order_details } from '../../store/reducers/orderReducer';
import { useCurrency } from '../../context/CurrencyContext';
import { ChevronLeft } from 'lucide-react';

const STATUS_COLOR = {
    paid:      'text-green-600 bg-green-50 border-green-200',
    pending:   'text-orange-500 bg-orange-50 border-orange-200',
    cancelled: 'text-red-500 bg-red-50 border-red-200',
    placed:    'text-blue-500 bg-blue-50 border-blue-200',
    warehouse: 'text-purple-500 bg-purple-50 border-purple-200',
    delivered: 'text-green-600 bg-green-50 border-green-200',
};

const StatusPill = ({ status }) => {
    const cls = STATUS_COLOR[status?.toLowerCase()] || 'text-gray-600 bg-gray-100 border-gray-200';
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border capitalize ${cls}`}>
            {status}
        </span>
    );
};

const OrderDetails = () => {
    const { orderId } = useParams();
    const dispatch = useDispatch();
    const { userInfo } = useSelector(s => s.auth);
    const { myOrder }  = useSelector(s => s.order);
    const { formatBase } = useCurrency();

    useEffect(() => {
        dispatch(get_order_details(orderId));
    }, [orderId]);

    return (
        <div className="space-y-3">
            {/* Back + title */}
            <div className="flex items-center gap-2">
                <Link
                    to="/dashboard/my-orders"
                    className="p-1 rounded text-gray-500 hover:bg-gray-200 transition-colors"
                >
                    <ChevronLeft className="w-5 h-5" />
                </Link>
                <div>
                    <h1 className="text-sm font-semibold text-gray-900">
                        Order #{myOrder._id?.slice(-8).toUpperCase()}
                    </h1>
                    <p className="text-xs text-gray-400">{myOrder.date}</p>
                </div>
            </div>

            <div className="grid grid-cols-3 md:grid-cols-1 gap-3">
                {/* Left — products */}
                <div className="col-span-2 md:col-span-1 space-y-3">
                    {/* Order items card */}
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                Order Items
                            </span>
                        </div>
                        <div className="divide-y divide-gray-100">
                            {myOrder.products?.map((p, i) => {
                                const final = p.price - Math.floor((p.price * p.discount) / 100);
                                return (
                                    <div key={i} className="flex items-center gap-3 px-4 py-3">
                                        <img
                                            src={p.images?.[0]}
                                            alt={p.name}
                                            className="w-16 h-16 object-cover rounded border border-gray-200 flex-shrink-0"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-gray-800 line-clamp-2 leading-snug">{p.name}</p>
                                            <p className="text-xs text-gray-400 mt-0.5">{p.brand}</p>
                                            <p className="text-xs text-gray-500 mt-0.5">Qty: {p.quantity}</p>
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <p className="text-sm font-bold text-gray-900">{formatBase(final)}</p>
                                            {p.discount > 0 && (
                                                <p className="text-xs text-gray-400 line-through">{formatBase(p.price)}</p>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Shipping info */}
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                Delivery Information
                            </span>
                        </div>
                        <div className="px-4 py-4 space-y-2.5">
                            <div className="flex gap-3">
                                <span className="text-xs text-gray-400 w-20 flex-shrink-0 pt-0.5">Name</span>
                                <span className="text-sm text-gray-800 font-medium">{myOrder.shippingInfo?.name}</span>
                            </div>
                            <div className="flex gap-3">
                                <span className="text-xs text-gray-400 w-20 flex-shrink-0 pt-0.5">Address</span>
                                <span className="text-sm text-gray-800">{myOrder.shippingInfo?.address}</span>
                            </div>
                            <div className="flex gap-3">
                                <span className="text-xs text-gray-400 w-20 flex-shrink-0 pt-0.5">City</span>
                                <span className="text-sm text-gray-800">
                                    {[myOrder.shippingInfo?.city, myOrder.shippingInfo?.province].filter(Boolean).join(', ')}
                                </span>
                            </div>
                            <div className="flex gap-3">
                                <span className="text-xs text-gray-400 w-20 flex-shrink-0 pt-0.5">Email</span>
                                <span className="text-sm text-gray-800">{userInfo?.email}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right — summary */}
                <div className="space-y-3">
                    {/* Order summary */}
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                Order Summary
                            </span>
                        </div>
                        <div className="px-4 py-4 space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-500">Total Amount</span>
                                <span className="text-sm font-bold text-gray-900">{formatBase(myOrder.price)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-500">Payment</span>
                                <StatusPill status={myOrder.payment_status} />
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-500">Delivery</span>
                                <StatusPill status={myOrder.delivery_status} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderDetails;
